import type { Bhed } from '../api/bhedApi';

export type BhedAttributeFilters = {
  hraswa: boolean;
  dirgha: boolean;
  pluta: boolean;
  anunasik: boolean;
  ananunasik: boolean;
  udatta: boolean;
  anudatta: boolean;
  swarit: boolean;
};

export const EMPTY_BHED_FILTERS: BhedAttributeFilters = {
  hraswa: false,
  dirgha: false,
  pluta: false,
  anunasik: false,
  ananunasik: false,
  udatta: false,
  anudatta: false,
  swarit: false,
};

export const BHED_FILTER_OPTIONS: { key: keyof BhedAttributeFilters; label: string; group: 'matra' | 'anunasik' | 'swor' }[] = [
  { key: 'hraswa', label: 'Hraswa', group: 'matra' },
  { key: 'dirgha', label: 'Dirgha', group: 'matra' },
  { key: 'pluta', label: 'Pluta', group: 'matra' },
  { key: 'anunasik', label: 'Anunasik', group: 'anunasik' },
  { key: 'ananunasik', label: 'Ananunasik', group: 'anunasik' },
  { key: 'udatta', label: 'Udatta', group: 'swor' },
  { key: 'anudatta', label: 'Anudatta', group: 'swor' },
  { key: 'swarit', label: 'Swarit', group: 'swor' },
];

export function hasActiveBhedFilters(filters: BhedAttributeFilters): boolean {
  return BHED_FILTER_OPTIONS.some((o) => filters[o.key]);
}

/**
 * Within matra / anunasik / swor: match at least one checked option in that group (OR).
 * Across groups: every group with a checked option must match (AND).
 * Unchecked groups do not restrict.
 */
export function bhedMatchesAttributeFilters(b: Bhed, filters: BhedAttributeFilters): boolean {
  const anyMatra = filters.hraswa || filters.dirgha || filters.pluta;
  if (anyMatra) {
    const ok =
      (filters.hraswa && b.matra === 'hraswa') ||
      (filters.dirgha && b.matra === 'dirgha') ||
      (filters.pluta && b.matra === 'pluta');
    if (!ok) return false;
  }
  const anyAnunasik = filters.anunasik || filters.ananunasik;
  if (anyAnunasik) {
    const ok =
      (filters.anunasik && b.anunasik === 'anunasik') ||
      (filters.ananunasik && b.anunasik === 'ananunasik');
    if (!ok) return false;
  }
  const anySwor = filters.udatta || filters.anudatta || filters.swarit;
  if (anySwor) {
    const ok =
      (filters.udatta && b.swor === 'udatta') ||
      (filters.anudatta && b.swor === 'anudatta') ||
      (filters.swarit && b.swor === 'swarit');
    if (!ok) return false;
  }
  return true;
}

export function bhedIdsForBarnaWithFilters(
  bhedRows: Bhed[],
  barnaId: number,
  filters: BhedAttributeFilters
): number[] {
  const barnaBheds = bhedRows.filter((h) => Number(h.barnaId) === barnaId);
  if (!hasActiveBhedFilters(filters)) {
    return barnaBheds.map((h) => h.id);
  }
  return barnaBheds.filter((b) => bhedMatchesAttributeFilters(b, filters)).map((b) => b.id);
}

export function normalizeBhedFilters(raw: unknown): BhedAttributeFilters {
  if (raw == null || typeof raw !== 'object') return { ...EMPTY_BHED_FILTERS };
  const o = raw as Record<string, unknown>;
  const out = { ...EMPTY_BHED_FILTERS };
  for (const opt of BHED_FILTER_OPTIONS) {
    out[opt.key] = Boolean(o[opt.key]);
  }
  return out;
}
